'use client';

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetFilterSetByIdQuery, useGetFilterSetsQuery } from 'src/api/redux/filterSets';
import {
  GenericNodeComponent,
  GenericSelectorModal,
  ConnectorType,
} from '../shared';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faChevronDown,
  faBuilding,
  faUser,
  faFilter,
} from '@fortawesome/pro-solid-svg-icons';
import { stripHtml } from 'src/utils/stripHtml';
import {
  FilterSetItem,
  filterSetNodeConfig,
  filterSetModalConfig,
  transformFilterSet,
} from './config';

interface FilterSetsComponentProps {
  componentId: string;
  initialFilterSets?: FilterSetItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const FilterSetsComponent: React.FC<FilterSetsComponentProps> = ({
  componentId,
  initialFilterSets = [],
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Initialize state - ui_metadata provides pre-hydrated objects
  const [filterSets, setFilterSets] =
    useState<FilterSetItem[]>(initialFilterSets);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch] = useDebounce(searchTerm, 300);
  const [expandedSection, setExpandedSection] = useState<
    'company' | 'personal' | null
  >('company');

  // Track if this is a new component from a placeholder that hasn't saved yet
  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && initialFilterSets.length === 0
  );

  // Check if we need to hydrate from ID
  const needsHydration =
    (initialFilterSets.length > 0 && typeof initialFilterSets[0] === 'string') ||
    typeof initialFilterSets[0] === 'number';

  const filterSetId = needsHydration ? String(initialFilterSets[0]) : null;

  const { data: hydratedData, isLoading: isHydrating } =
    useGetFilterSetByIdQuery(
      { id: filterSetId as string },
      { skip: !needsHydration || !filterSetId }
    );

  // Fetch filter sets for the modal
  const { data: filterSetsData, isLoading: isSearching } = useGetFilterSetsQuery({
    q: debouncedSearch || '',
    serializer: 'detailed',
  });

  // Filter to exclude CODE type and transform to FilterSetItem format
  const searchResults: FilterSetItem[] = useMemo(() => {
    const filterSetsFromApi =
      filterSetsData?.results?.filter((fs) => fs.type !== 'CODE') || [];
    return filterSetsFromApi.map((fs) => transformFilterSet(fs as any));
  }, [filterSetsData?.results]);

  // Group filter sets by scope for custom rendering
  const groupedFilterSets = useMemo(() => {
    const companyFilterSets = searchResults
      .filter((fs) => fs.scope === 'COMPANY')
      .sort((a, b) => a.label.localeCompare(b.label));

    const personalFilterSets = searchResults
      .filter((fs) => fs.scope !== 'COMPANY')
      .sort((a, b) => a.label.localeCompare(b.label));

    return { companyFilterSets, personalFilterSets };
  }, [searchResults]);

  // Hydrate the filter set data when it loads
  useEffect(() => {
    if (needsHydration && hydratedData) {
      const transformed = transformFilterSet(hydratedData as any);
      setFilterSets([transformed]);
    }
  }, [needsHydration, hydratedData]);

  // Auto-open modal if starting with no filter sets (new component)
  useEffect(() => {
    if (initialFilterSets.length === 0 && !isHydrating) {
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync when external items are added (slash menu merge)
  useEffect(() => {
    if (initialFilterSets.length > filterSets.length) {
      setFilterSets(initialFilterSets);
    }
  }, [initialFilterSets.length]);

  const handleSave = useCallback(
    (newFilterSets: FilterSetItem[], connector: ConnectorType) => {
      if (!newFilterSets || newFilterSets.length === 0) {
        onRemove(componentId);
        setShowModal(false);
        return;
      }

      const selectedFilterSet = newFilterSets[0];
      setFilterSets([selectedFilterSet]);

      // Clear the new-from-placeholder flag since we have data now
      setIsNewFromPlaceholder(false);

      // FilterSets always use 'ALL' connector (single selection), but we accept the param for type compatibility
      onUpdate(componentId, {
        type: 'filter_set',
        value: [selectedFilterSet.id],
        ui_metadata: [selectedFilterSet], // Store full object for hydration-free loading
        connector: connector || 'ALL',
      });

      setShowModal(false);
    },
    [componentId, onUpdate, onRemove]
  );

  // Handle modal close - if from placeholder with no data, remove component
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    // If this was new from a placeholder and user closed without selecting, remove it
    if (isNewFromPlaceholder && filterSets.length === 0) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, filterSets.length, onRemove, componentId]);

  const handleRemoveItem = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  // Custom render function for grouped results (Company/Personal sections)
  const renderGroupedResults = useCallback(
    ({
      selectedItems,
      onSelect,
      isLoading,
      searchTerm: currentSearchTerm,
    }: {
      results: FilterSetItem[];
      selectedItems: FilterSetItem[];
      onSelect: (item: FilterSetItem) => void;
      onDeselect: (item: FilterSetItem) => void;
      isLoading: boolean;
      searchTerm: string;
    }) => {
      const hasResults =
        groupedFilterSets.companyFilterSets.length > 0 ||
        groupedFilterSets.personalFilterSets.length > 0;

      if (isLoading) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in duration-300">
              <div className="animate-pulse text-neutral-400">
                Loading filter sets...
              </div>
            </div>
          </div>
        );
      }

      if (!hasResults) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-500 border border-neutral-200 rounded-lg bg-neutral-50/50">
            <div className="text-center animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex flex-col items-center gap-3">
                <Icon icon={faFilter} className="w-12 h-12 text-neutral-300" />
                <div className="flex flex-col items-center gap-1">
                  <div className="text-sm">
                    {currentSearchTerm
                      ? `No filter sets found for "${currentSearchTerm}"`
                      : 'No filter sets available'}
                  </div>
                  {currentSearchTerm && (
                    <div className="text-xs text-neutral-400">
                      Try adjusting your search terms
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      const renderSection = (
        title: string,
        icon: typeof faBuilding,
        items: FilterSetItem[],
        sectionKey: 'company' | 'personal'
      ) => {
        if (items.length === 0) return null;

        return (
          <div key={sectionKey}>
            <button
              type="button"
              onClick={() =>
                setExpandedSection(
                  expandedSection === sectionKey ? null : sectionKey
                )
              }
              className="w-full bg-neutral-100 px-4 py-2.5 border-b border-neutral-200 flex items-center justify-between gap-2 hover:bg-neutral-200 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Icon icon={icon} className="w-3.5 h-3.5 text-neutral-500" />
                <span className="text-sm font-semibold text-neutral-700">
                  {title}
                </span>
                <span className="text-xs text-neutral-500">
                  ({items.length})
                </span>
              </div>
              <Icon
                icon={faChevronDown}
                className={`w-3 h-3 text-neutral-500 transition-transform duration-200 ${
                  expandedSection === sectionKey ? 'rotate-180' : ''
                }`}
              />
            </button>
            {expandedSection === sectionKey && (
              <div>
                {items.map((item) => {
                  const isSelected = selectedItems.some(
                    (s) => s.id === item.id
                  );
                  return (
                    <div
                      key={item.id}
                      onClick={() => onSelect(item)}
                      className={`p-4 border-b border-neutral-100 last:border-b-0 cursor-pointer transition-all duration-300 hover:bg-neutral-50 ${
                        isSelected ? 'bg-indigo-50 hover:bg-indigo-100' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ${
                              isSelected
                                ? 'border-indigo-600 bg-indigo-600'
                                : 'border-neutral-300'
                            }`}
                          >
                            {isSelected && (
                              <Icon
                                icon={faCheck}
                                className="w-2.5 h-2.5 text-white"
                              />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`font-medium text-sm truncate ${
                                isSelected
                                  ? 'text-indigo-800'
                                  : 'text-neutral-900'
                              }`}
                            >
                              {stripHtml(item.label)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      };

      return (
        <div className="h-full overflow-y-auto border border-neutral-200 rounded-lg bg-white">
          {renderSection(
            'Company',
            faBuilding,
            groupedFilterSets.companyFilterSets,
            'company'
          )}
          {renderSection(
            'Personal',
            faUser,
            groupedFilterSets.personalFilterSets,
            'personal'
          )}
        </div>
      );
    },
    [groupedFilterSets, expandedSection]
  );

  // Create config with custom render function
  const modalConfigWithRender = useMemo(
    () => ({
      ...filterSetModalConfig,
      renderResultsSection: renderGroupedResults,
    }),
    [renderGroupedResults]
  );

  // Show loading state while hydrating data
  if (isHydrating) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500">
        Loading filter set...
      </span>
    );
  }

  return (
    <>
      {filterSets.length > 0 ? (
        <GenericNodeComponent
          config={filterSetNodeConfig}
          items={filterSets}
          connector="ALL"
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-full cursor-pointer hover:bg-indigo-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-indigo-600 text-xs font-medium">
            Select filter set...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={modalConfigWithRender}
        isOpen={showModal}
        selectedItems={filterSets}
        connector="ALL"
        isEditing={filterSets.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isSearching}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default FilterSetsComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderFilterSetsComponent = (
  container: HTMLElement,
  props: FilterSetsComponentProps & {
    initialFilterSets?: FilterSetItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <FilterSetsComponent {...props} />
    </Provider>
  );
  return root;
};
